<template>
  <w-setting-sliding-popup ref="schedulePopup" :title="title" width="600px" @close="onClosePopup">
    <el-form slot="content" :model="scheduleData">
      <div class="title-bar title-h4 dot-title">수집 주기 정보</div>
      <div class="mr-b25">
        <el-form-item label="스케줄 명" prop="scheduleName" :rules="[{ required: true, message: '스케줄 명을 입력하십시오.', trigger: ['change', 'blur'] }]">
          <el-input v-model="scheduleData.scheduleName" />
        </el-form-item>
      </div>

      <div class="title-bar title-h4 dot-title">주기 설정</div>
      <w-period-setting-component class="mr-b25"  ref="periodSettingCmp" />
      <w-common-obj-list ref="commonObjList" type="physical" :selected-managetype-id="managetypeId" :load-obj-list="true" :load-params="loadParams" :active="active">
        <div slot="title" class="title-h4 dot-title">
          수집 대상 선택
        </div>
      </w-common-obj-list>
    </el-form>

    <el-row slot="footer" type="flex" justify="center">
      <el-button class="mr-r4" type="solid" size="large" @click="onClickSaveButton">저장</el-button>
      <el-button size="large" @click="onClosePopup">취소</el-button>
    </el-row>
  </w-setting-sliding-popup>
</template>

<script>
import { clone, differenceWith, isEmpty as objectIsEmpty } from 'lodash';

import ScheduleAPI from '@/api/settings/config-schedule';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import { isEmpty } from '@/utils/basic';
import msgUtil from '@/utils/message';
import WPeriodSettingComponent from '@/views/settings/components/PeriodSettingComponent';
import WCommonObjList from '@/components/table/CommonObjList';

export default {
  name: 'WScheduleSettingPopup',
  components: {
    WPeriodSettingComponent,
    WCommonObjList,
  },
  props: {
    managetypeId: String,
    configSchedule: Object,
    popupType: String,
  },
  data() {
    return {
      active: false,
      scheduleData: {},
      title: '수집 주기 추가',
    };
  },
  computed: {
    valid() {
      return !isEmpty(this.scheduleData.scheduleName);
    },
    loadParams() {
      if (objectIsEmpty(this.scheduleData)) {
        return {};
      }
      return {
        objIds: this.scheduleData.objIds,
      };
    },
  },
  watch: {
    popupType(type) {
      if (type === 'add') {
        this.title = '수집 주기 추가';
      } else if (type === 'edit') {
        this.title = '수집 주기 편집';
      }
    },
    configSchedule(data) {
      this.scheduleData = clone(data);
      if (objectIsEmpty(data)) {
        return;
      }
      this.$nextTick(() => {
        this.$refs.periodSettingCmp.setPeriodSettingData(data);
      });
    },
  },
  methods: {
    /**
     * 수집 주기 팝업창을 띄운다.
     * @return {[type]} [description]
     * @author jda
     */
    open() {
      this.active = true;
      this.$refs.schedulePopup.visible = true;
    },
    /**
     * 수집 주기 팝업창을 닫는다.
     * @return {[type]} [description]
     * @author jda
     */
    onClosePopup() {
      this.active = false;
      this.reset();
      this.$refs.schedulePopup.visible = false;
    },
    /**
     * 수집 주기 정보를 저장한다.
     * @return {[type]} [description]
     * @author jda
     */
    onClickSaveButton() {
      if (!this.valid) {
        this.$message(msgUtil.getCustomMsgData('값을 입력해주세요.'));
        return;
      }
      const originalScheduleData = this.scheduleData;
      // 수집 대상
      const checkedTarget = this.$refs.commonObjList.getSelectedObjList(true);
      if (checkedTarget.length < 1) {
        this.$message(msgUtil.getCustomMsgData('대상을 선택해주세요.'));
        return;
      }
      // 주기설정 데이터
      const data = this.$refs.periodSettingCmp.getPeriodSettingData();
      const params = Object.assign(data, { objIds: checkedTarget, managetypeId: this.managetypeId, scheduleContent: this.createScheduleContent(data) });

      const actionTarget = '스케줄';
      if (this.popupType === 'add') {
        Object.assign(params, originalScheduleData);
        const action = msgUtil.action.ADD;
        ScheduleAPI.addConfigurationSchedule(params).then((response) => {
          if (response.data.success) {
            this.$message(msgUtil.getResultMsgData(action, actionTarget, true));
            this.$emit('loadData');
            this.onClosePopup();
          } else {
            this.$message(msgUtil.getResultMsgData(action, actionTarget, false));
          }
        }).catch(() => {
          this.$message(msgUtil.getResultMsgData(action, actionTarget, false));
        });
      } else {
        // 주기가 다를 경우 기존 주기 설정 값은 null로 셋팅
        if (originalScheduleData.schedulePeriod !== params.schedulePeriod) {
          const deletedKeys = this.$refs.periodSettingCmp.getKeysByPeriod(originalScheduleData.schedulePeriod);
          deletedKeys.forEach((key) => {
            originalScheduleData[key] = null;
          });
        }

        const deletedObjIds = differenceWith(originalScheduleData.objIds, params.objIds);
        const addedObjIds = differenceWith(params.objIds, originalScheduleData.objIds);
        const action = msgUtil.action.SAVE;
        ScheduleAPI.updateConfigurationSchedule(Object.assign({}, originalScheduleData, params, { deletedObjIds, addedObjIds })).then((response) => {
          if (response.data.success) {
            this.$message(msgUtil.getResultMsgData(action, actionTarget, true));
            this.$emit('loadData');
            this.onClosePopup();
          } else {
            this.$message(msgUtil.getResultMsgData(action, actionTarget, false));
          }
        }).catch(() => {
          this.$message(msgUtil.getResultMsgData(action, actionTarget, false));
        });
      }
    },
    /**
     * 스케쥴 상세내용
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    createScheduleContent(record) {
      let scheduleContent;

      switch (record.schedulePeriod) {
        case 'N':
          scheduleContent = `${getDateToString(record.scheduleStarttime, 'YYYY년 MM월 DD일 HH:mm')}수행`;
          break;
        case 'D': {
          let hourInfo = record.scheduleHour;
          let minInfo = record.scheduleMinute;
          if (record.scheduleHour < 10) {
            hourInfo = `0${record.scheduleHour}`;
          }
          if (record.scheduleMinute < 10) {
            minInfo = `0${record.scheduleMinute}`;
          }
          scheduleContent = `매일 ${hourInfo} : ${minInfo}마다 수행`;
          break;
        }
        case 'W': {
          const days = ['일', '월', '화', '수', '목', '금', '토'];

          let intervalDate;
          record.scheduleIntervalDate.split(',').forEach((date) => {
            if (isEmpty(intervalDate)) {
              intervalDate = days[parseInt(date, 10) - 1];
            } else {
              intervalDate += `, ${days[parseInt(date, 10) - 1]}`;
            }
          });
          let hourInfo = record.scheduleHour;
          let minInfo = record.scheduleMinute;
          if (record.scheduleHour < 10) {
            hourInfo = `0${record.scheduleHour}`;
          }
          if (record.scheduleMinute < 10) {
            minInfo = `0${record.scheduleMinute}`;
          }
          scheduleContent = `매주 ${intervalDate}요일 ${hourInfo} : ${minInfo}마다 수행`;
          break;
        }
        case 'M': {
          let hourInfo = record.scheduleHour;
          let minInfo = record.scheduleMinute;
          if (record.scheduleHour < 10) {
            hourInfo = `0${record.scheduleHour}`;
          }
          if (record.scheduleMinute < 10) {
            minInfo = `0${record.scheduleMinute}`;
          }
          scheduleContent = `매 ${record.scheduleIntervalMonth}월 ${record.scheduleIntervalDate}일에 ${hourInfo} : ${minInfo}마다 수행`;
          break;
        }
        default:
          scheduleContent = EMPTY_TEXT;
          break;
      }

      return scheduleContent;
    },
    /**
     * 팝업창을 초기화한다.
     * @author jda
     */
    reset() {
      this.$emit('resetScheduleData');
      this.$refs.periodSettingCmp.init();
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/operation/ScheduleSettings/popup/ScheduleSettingPopup.vue